import React, { useState, useEffect } from "react";
import { Organization } from "../../../../../core/models/entities/Organization";
import { useOrganizationRepository } from "../../../../../data/repositories/OrganizationRepository";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faArrowRight,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useWhiteLabelContext } from "../../../../../WhiteLabelContext";
import { useTranslation } from "react-i18next";

interface VendorSearchFilterProps {
  open?: boolean;
  initialValue?: string | number | boolean;
  onOpenToggle: (open: boolean) => void;
  onOptionSelect: (event: {
    value: string | number | boolean | undefined;
    key: string;
  }) => void;
  onRemoveFilter?: (key: string) => void;
  filterKey: string;
  defaultTitle: string;
}

export function VendorSearchFilter({
  open = false,
  initialValue,
  onOpenToggle,
  onOptionSelect,
  onRemoveFilter,
  filterKey,
  defaultTitle,
}: VendorSearchFilterProps) {
  const { t } = useTranslation();
  const [show, setShow] = useState(open);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [searchResults, setSearchResults] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Organization | null>(
    null
  );
  const organizationRepository = useOrganizationRepository();
  const { whiteLabel } = useWhiteLabelContext();

  useEffect(() => {
    setShow(open);
  }, [open]);

  // Initialize selected vendor from initialValue
  useEffect(() => {
    if (initialValue && !selectedVendor) {
      // If we have an initial value, we need to find the vendor by ID
      // For now, we'll just show the ID until we can fetch the vendor details
      // In a real implementation, you might want to fetch the vendor details
      setSelectedVendor({
        id: Number(initialValue),
        name: t("compGenericTable.idLabel", { id: initialValue }),
      } as Organization);
    } else if (!initialValue && selectedVendor) {
      // Reset when initialValue is cleared (e.g., during filter reset)
      setSelectedVendor(null);
      setInputValue("");
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [initialValue, selectedVendor]);

  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await organizationRepository.searchAdminVendors(query);
      setSearchResults(results);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching vendors:", error);
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(inputValue);
    performSearch(inputValue);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    if (hasSearched) {
      setHasSearched(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleOpenClick = () => {
    const newShow = !show;
    onOpenToggle(newShow);
    setShow(newShow);

    if (newShow) {
      // Clear search when opening
      setInputValue("");
      setSearchResults([]);
      setHasSearched(false);
    }
  };

  const handleVendorSelect = (vendor?: Organization) => {
    setSelectedVendor(vendor || null);
    setShow(false);
    onOpenToggle(false);

    // Notify parent component of selection
    onOptionSelect({
      value: vendor ? vendor.id : undefined,
      key: filterKey,
    });

    // Clear search state
    setInputValue("");
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleClearSelection = () => {
    handleVendorSelect(undefined);
  };

  // Clear results when input is empty
  useEffect(() => {
    if (!inputValue.trim()) {
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [inputValue]);

  return (
    <div className="button-root w-48 px-0 relative !text-xs">
      <div className="relative">
        <button
          style={{
            color: selectedVendor || show ? whiteLabel.colorHex : undefined,
          }}
          className={`button-root w-48 z-0 dropdown-button ${
            show ? "rounded-b-none" : ""
          }`}
          onClick={handleOpenClick}
        >
          <label className="max-w-full overflow-hidden truncate">
            {selectedVendor ? selectedVendor.name : defaultTitle}
          </label>
        </button>
        {selectedVendor && onRemoveFilter && (
          <button
            onClick={() => onRemoveFilter(filterKey)}
            className="absolute -top-1 -right-1 w-3 h-3 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-500 transition-colors duration-200 z-10"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>
        )}
      </div>

      {show && (
        <div className="w-full z-10 absolute bg-gray-100 text-gray-600 border border-gray-200 rounded-md rounded-t-none border-t-0 text-xs text-center top-full">
          {/* Search input section */}
          <div className="p-2 border-b bg-white">
            <div className="border rounded-md border-gray-200 border-solid h-8 flex space-x-2 items-center pl-2 bg-white focus-within:border-gray-300 relative">
              <span>
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="text-gray-500 text-xs"
                />
              </span>
              <input
                className="border-none text-xs font-medium outline-none w-full pr-8"
                placeholder={t("compGenericTable.searchForVendor")}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={isLoading || !inputValue.trim()}
                className="absolute right-2 p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin text-gray-500 text-xs"
                  />
                ) : hasSearched ? null : (
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-gray-500 text-xs"
                  />
                )}
              </button>
            </div>
          </div>

          {/* Search results */}
          {hasSearched && (
            <ul className="w-full overflow-y-auto max-h-72">
              {searchResults.length > 0 ? (
                searchResults.map((vendor) => (
                  <li
                    key={vendor.id}
                    onClick={() => handleVendorSelect(vendor)}
                    className="flex justify-center px-4 py-2 cursor-pointer text-gray-600 hover:text-white hover:bg-primary-100"
                  >
                    {vendor.name}
                  </li>
                ))
              ) : (
                <li className="flex justify-center px-4 py-2 text-gray-600 italic">
                  {t("compGenericTable.noVendorsFound")}
                </li>
              )}
            </ul>
          )}

          {/* Show message when no search has been performed or clear filter option */}
          {!isLoading && selectedVendor ? (
            <div className="p-2 text-xs">
              <button
                onClick={handleClearSelection}
                className="text-gray-500 hover:text-gray-700 italic cursor-pointer"
              >
                {t("compGenericTable.clearFilter")}
              </button>
            </div>
          ) : !hasSearched && !isLoading ? (
            <div className="p-2 text-xs">
              <span className="text-gray-500 italic">
                {t("compGenericTable.pressEnterToSearch")}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
